import React, { useState, useMemo } from 'react';
import { GuestEvent } from '../../services/guestAPI';
import { GuestCard } from './GuestCard';
import { GuestCommentModal } from './GuestCommentModal';
import { useGuestDisplayNames } from '../../hooks/useGuestDisplayName';
import { formatGuestEventDisplayName } from '../../utils/guestNameUtils';
import { useAppSelector } from '../../store/hooks';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface GuestDisplayProps {
  guests: GuestEvent[];
  eventId: string;
  viewMode: 'grid' | 'table' | 'list';
  loading?: boolean;
  selectedGuests: Set<string>;
  selectingAll?: boolean;
  onSelectGuest: (guestId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void | Promise<void>;
  onStatusUpdate: (guestId: number, status: GuestEvent['status']) => void;
  onDeleteGuest: (guestId: number, guestName: string) => void;
  onEditCompanions?: (guestId: number, guest: GuestEvent) => void;
  onSendReminder?: (guestId: string) => void;
  onError?: (message: string) => void;
  onRefresh?: () => void;
  theme: 'dark' | 'light';
  eventHasEnded?: boolean;
  pagination?: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
  };
}

const LoadingSkeleton: React.FC<{ viewMode: 'grid' | 'table' | 'list'; theme: 'dark' | 'light' }> = ({ viewMode, theme }) => {
  const count = viewMode === 'grid' ? 8 : 6;
  
  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: count }).map((_, index) => (
          <div key={index} className={`border rounded-lg shadow-sm animate-pulse ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-gray-200'
          }`}>
            {/* Header */}
            <div className={`p-3 border-b ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 flex-1">
                  <div className={`w-4 h-4 rounded ${
                    theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                  }`}></div>
                  <div className={`h-4 rounded w-32 ${
                    theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                  }`}></div>
                </div>
                <div className={`w-4 h-4 rounded ${
                  theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                }`}></div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-3">
              <div className={`h-6 rounded-full w-20 mb-3 ${
                theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
              }`}></div>
              <div className="space-y-2 mb-3">
                <div className={`h-4 rounded w-full ${
                  theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                }`}></div>
                <div className={`h-4 rounded w-4/5 ${
                  theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                }`}></div>
              </div>
              <div className={`h-3 rounded w-24 ${
                theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
              }`}></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 bg-gray-200 rounded"></div>
              <div>
                <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
            <div className="h-6 bg-gray-200 rounded-full w-20"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

const EmptyState: React.FC<{ hasFilters: boolean; theme: 'dark' | 'light' }> = ({ hasFilters, theme }) => (
  <div className="text-center py-12">
    <div className={`mx-auto h-24 w-24 mb-4 ${
      theme === 'dark' ? 'text-gray-500' : 'text-gray-300'
    }`}>
      <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>
    </div>
    <h3 className={`text-lg font-medium mb-2 ${
      theme === 'dark' ? 'text-white' : 'text-gray-900'
    }`}>
      {hasFilters ? 'No guests found' : 'No guests invited yet'}
    </h3>
    <p className={`mb-6 ${
      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
    }`}>
      {hasFilters 
        ? 'Try adjusting your filters or search terms to find guests.'
        : 'Start by adding guests manually or importing from a file.'
      }
    </p>
  </div>
);

const Pagination: React.FC<NonNullable<GuestDisplayProps['pagination']>> = ({
  currentPage,
  totalPages,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange
}) => {
  const pageSizeOptions = [12, 21, 24, 48, 96];
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => onPageChange(1)}
          className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <span key="ellipsis1" className="px-3 py-2 text-sm text-gray-400">
            ...
          </span>
        );
      }
    }

    // Add visible page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => onPageChange(i)}
          className={`px-3 py-2 text-sm font-medium rounded-lg ${
            i === currentPage
              ? 'bg-blue-500 text-white'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          {i}
        </button>
      );
    }

    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="ellipsis2" className="px-3 py-2 text-sm text-gray-400">
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => onPageChange(totalPages)}
          className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-200">
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-700">
          Showing {startItem} to {endItem} of {totalCount} guests
        </span>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700">Show:</label>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        
        <div className="flex items-center gap-1">
          {renderPageNumbers()}
        </div>
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export const GuestDisplay: React.FC<GuestDisplayProps> = ({
  guests,
  eventId,
  viewMode,
  loading = false,
  selectedGuests,
  selectingAll = false,
  onSelectGuest,
  onSelectAll,
  onStatusUpdate,
  onDeleteGuest,
  onEditCompanions,
  onSendReminder,
  onError,
  onRefresh,
  theme,
  eventHasEnded = false,
  pagination
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState<GuestEvent | null>(null);
  
  const eventGuestCount = pagination?.totalCount ?? guests.length;
  const allSelected = eventGuestCount > 0 && selectedGuests.size === eventGuestCount;
  const someSelected = selectedGuests.size > 0 && !allSelected;
  
  const handleEditComments = (guest: GuestEvent) => {
    setEditingGuest(guest);
    setCommentModalOpen(true);
  };
  
  const handleCommentUpdate = (updatedGuest: GuestEvent) => {
    // Refresh the guest list to show updated comments
    if (onRefresh) {
      onRefresh();
    }
    setCommentModalOpen(false);
    setEditingGuest(null);
  };

  // Get display names for all guests - only fetch for guests without guest_name field
  // guest_name from API now includes nickname-aware names, so we only need to fetch for missing ones
  const guestIds = useMemo(() => 
    guests
      .filter(guest => !guest.guest_name && guest.guest?.id)
      .map(guest => guest.guest!.id)
      .filter(Boolean) as string[],
    [guests]
  );
  const { getDisplayName } = useGuestDisplayNames(guestIds);

  const handleSelectAll = (selected: boolean) => {
    onSelectAll(selected);
  };

  if (loading) {
    return <LoadingSkeleton viewMode={viewMode} theme={theme} />;
  }

  if (guests.length === 0) {
    return <EmptyState hasFilters={false} theme={theme} />;
  }

  const renderGridView = () => (
    <div className="space-y-4">
      {/* Select All Header Bar for Grid View */}
      <div className={`rounded-lg border p-3 ${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={allSelected}
              disabled={eventHasEnded || selectingAll || eventGuestCount === 0}
              ref={input => {
                if (input) input.indeterminate = someSelected && !allSelected;
              }}
              onChange={(e) => {
                if (eventHasEnded || selectingAll) return;
                void handleSelectAll(e.target.checked);
              }}
              className={`h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500 rounded-md transition-all duration-200 ${
                eventHasEnded || selectingAll
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:border-blue-400 cursor-pointer'
              } ${
                theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
              }`}
              title={eventHasEnded ? "Cannot select guests for events that have already ended" : (allSelected ? "Deselect all" : "Select all")}
            />
            <span className={`text-sm font-medium ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {selectingAll
                ? 'Selecting all guests...'
                : allSelected 
                ? `All ${eventGuestCount} guest${eventGuestCount > 1 ? 's' : ''} selected`
                : someSelected
                ? `${selectedGuests.size} of ${eventGuestCount} selected`
                : `Select all ${eventGuestCount} guest${eventGuestCount > 1 ? 's' : ''}`}
            </span>
          </div>
          {someSelected && !allSelected && !selectingAll && (
            <button
              onClick={() => {
                if (eventHasEnded || selectingAll) return;
                void handleSelectAll(true);
              }}
              className={`text-xs font-medium px-2 py-1 rounded transition-colors ${
                theme === 'dark'
                  ? 'text-blue-400 hover:text-blue-300 hover:bg-gray-700'
                  : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
              }`}
            >
              Select All
            </button>
          )}
        </div>
      </div>

      {/* Grid of Guest Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {guests.map(guest => (
          <GuestCard
            key={guest.id}
            guest={guest}
            eventId={eventId}
            onStatusUpdate={onStatusUpdate}
            onDelete={onDeleteGuest}
            onSelect={onSelectGuest}
            onEditCompanions={onEditCompanions}
            onEditComments={handleEditComments}
            onSendReminder={onSendReminder}
            onError={onError}
            selected={selectedGuests.has(guest.id.toString())}
            eventHasEnded={eventHasEnded}
          />
        ))}
      </div>
    </div>
  );

  const renderListView = () => (
    <div className="space-y-4">
      {guests.map(guest => (
        <GuestCard
          key={guest.id}
          guest={guest}
          eventId={eventId}
          onStatusUpdate={onStatusUpdate}
          onDelete={onDeleteGuest}
          onSelect={onSelectGuest}
          onEditCompanions={onEditCompanions}
          onEditComments={handleEditComments}
          onSendReminder={onSendReminder}
          onError={onError}
          selected={selectedGuests.has(guest.id.toString())}
          compact
        />
      ))}
    </div>
  );

  const renderTableView = () => {
    // Mobile card view for table
    const renderMobileCard = (guest: GuestEvent) => {
      // Prefer guest_name from API (nickname-aware), then fallback to displayName hook, then fullName
      const displayName = guest.guest?.id ? getDisplayName(guest.guest.id) : null;
      const guestName = formatGuestEventDisplayName(guest, displayName);
      
      return (
        <div key={guest.id} className={`border rounded-lg p-4 space-y-3 ${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        }`}>
          {/* Header Row */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <input
                type="checkbox"
                checked={selectedGuests.has(guest.id.toString())}
                disabled={eventHasEnded}
                onChange={(e) => {
                  if (eventHasEnded) return;
                  onSelectGuest(guest.id.toString(), e.target.checked);
                }}
                className={`h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500 rounded-md transition-all duration-200 flex-shrink-0 ${
                  eventHasEnded 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:border-blue-400 cursor-pointer'
                } ${
                  theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
                }`}
                title={eventHasEnded ? "Cannot select guests for events that have already ended" : ""}
              />
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-sm mb-1 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`} title={guestName}>
                  {guestName}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {guest.is_imported ? (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                      theme === 'dark'
                        ? 'bg-blue-900/30 text-blue-300 border-blue-700/50'
                        : 'bg-blue-100 text-blue-700 border-blue-200'
                    }`}>
                      Imported
                    </span>
                  ) : (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                      theme === 'dark'
                        ? 'bg-green-900/30 text-green-300 border-green-700/50'
                        : 'bg-green-100 text-green-700 border-green-200'
                    }`}>
                      Manual
                    </span>
                  )}
                </div>
              </div>
            </div>
            <span className={`inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-semibold shadow-sm border flex-shrink-0 ${
              guest.status === 'accepted' 
                ? (theme === 'dark' 
                    ? 'bg-green-900/30 text-green-300 border-green-700/50' 
                    : 'bg-green-100 text-green-800 border-green-200')
                : guest.status === 'rejected' 
                  ? (theme === 'dark' 
                      ? 'bg-red-900/30 text-red-300 border-red-700/50' 
                      : 'bg-red-100 text-red-800 border-red-200')
                  : guest.status === 'pending' 
                    ? (theme === 'dark' 
                        ? 'bg-yellow-900/30 text-yellow-300 border-yellow-700/50' 
                        : 'bg-yellow-100 text-yellow-800 border-yellow-200')
                    : (theme === 'dark' 
                        ? 'bg-gray-700 text-gray-300 border-gray-600' 
                        : 'bg-gray-100 text-gray-800 border-gray-200')
            }`}>
              {guest.status === 'accepted' ? 'Confirmed' :
               guest.status === 'rejected' ? 'Declined' :
               guest.status === 'pending' ? 'Pending' : 'No Response'}
            </span>
          </div>

          {/* Contact Info */}
          {(guest.guest?.phone || guest.guest?.email) && (
            <div className="space-y-1">
              {guest.guest?.phone && (
                <div className={`text-xs ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`} title={guest.guest.phone}>
                  📞 {guest.guest.phone}
                </div>
              )}
              {guest.guest?.email && (
                <div className={`text-xs ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`} title={guest.guest.email}>
                  ✉️ {guest.guest.email}
                </div>
              )}
            </div>
          )}

          {/* Footer Row */}
          <div className={`flex items-center justify-between pt-2 border-t ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className="flex items-center gap-2">
              {guest.companion_count > 0 ? (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                  theme === 'dark'
                    ? 'bg-purple-900/30 text-purple-300 border-purple-700/50'
                    : 'bg-purple-100 text-purple-700 border-purple-200'
                }`} title={`${guest.companion_count} companion${guest.companion_count > 1 ? 's' : ''}`}>
                  👥 {guest.companion_count}
                </span>
              ) : (
                <span className={`text-xs ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  👥 0
                </span>
              )}
            </div>
            <button
              onClick={() => onDeleteGuest(guest.id, guestName)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 ${
                theme === 'dark'
                  ? 'text-red-400 hover:text-white bg-red-900/20 hover:bg-red-500 border border-red-700/50 hover:border-red-500'
                  : 'text-red-600 hover:text-white bg-red-50 hover:bg-red-500 border border-red-200 hover:border-red-500'
              }`}
              title="Remove Guest"
            >
              Remove
            </button>
          </div>
        </div>
      );
    };

    return (
      <>
        {/* Mobile Card View */}
        <div className="block md:hidden space-y-3">
          {guests.map(guest => renderMobileCard(guest))}
        </div>

        {/* Desktop Table View */}
        <div className={`hidden md:block rounded-lg border overflow-hidden ${
          theme === 'dark' 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        }`}>
          {/* Enhanced Table Header */}
          <div className={`border-b ${
            theme === 'dark'
              ? 'bg-gradient-to-r from-gray-700 to-gray-600 border-gray-600'
              : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'
          }`}>
            <div className={`grid grid-cols-8 gap-3 px-4 py-3 text-xs font-medium uppercase tracking-wide ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              <div className="col-span-1 flex items-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  disabled={eventHasEnded || selectingAll || eventGuestCount === 0}
                  ref={input => {
                    if (input) input.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={(e) => {
                    if (eventHasEnded || selectingAll) return;
                    void handleSelectAll(e.target.checked);
                  }}
                  className={`h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500 rounded-md transition-all duration-200 ${
                    eventHasEnded 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:border-blue-400'
                  } ${
                    theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
                  }`}
                  title={eventHasEnded ? "Cannot select guests for events that have already ended" : ""}
                />
              </div>
              <div className="col-span-2 flex items-center">
                <span className="flex items-center space-x-1">
                  <span>Guest</span>
                  <div className={`h-1 w-1 rounded-full ${
                    theme === 'dark' ? 'bg-gray-500' : 'bg-gray-400'
                  }`}></div>
                </span>
              </div>
              <div className="col-span-2 flex items-center">
                <span className="flex items-center space-x-1">
                  <span>Contact</span>
                  <div className={`h-1 w-1 rounded-full ${
                    theme === 'dark' ? 'bg-gray-500' : 'bg-gray-400'
                  }`}></div>
                </span>
              </div>
              <div className="col-span-1 flex items-center">
                <span>Companions</span>
              </div>
              <div className="col-span-1 flex items-center">
                <span>Status</span>
              </div>
              <div className="col-span-1 text-center">
                <span>Actions</span>
              </div>
            </div>
          </div>

          {/* Compact Table Body */}
          <div className={`divide-y ${
            theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
          }`}>
            {guests.map(guest => {
              const displayName = guest.guest?.id ? getDisplayName(guest.guest.id) : null;
              const guestName = formatGuestEventDisplayName(guest, displayName);
              return (
                <div key={guest.id} className={`grid grid-cols-8 gap-3 px-4 py-3 items-center text-sm transition-all duration-200 border-l-4 border-transparent hover:border-blue-400 ${
                  theme === 'dark'
                    ? 'hover:bg-gradient-to-r hover:from-gray-700 hover:to-gray-600'
                    : 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50'
                }`}>
                  {/* Enhanced Checkbox */}
                  <div className="col-span-1 flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedGuests.has(guest.id.toString())}
                      disabled={eventHasEnded}
                      onChange={(e) => {
                        if (eventHasEnded) return;
                        onSelectGuest(guest.id.toString(), e.target.checked);
                      }}
                      className={`h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500 rounded-md transition-all duration-200 ${
                        eventHasEnded 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:border-blue-400 cursor-pointer'
                      } ${
                        theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
                      }`}
                      title={eventHasEnded ? "Cannot select guests for events that have already ended" : ""}
                    />
                  </div>

                  {/* Guest Name & Info */}
                  <div className="col-span-2">
                    <div className="space-y-1">
                      <div className={`font-semibold truncate text-sm ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`} title={guestName}>
                        {guestName}
                      </div>
                      <div className="flex items-center space-x-2">
                        {guest.is_imported ? (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                            theme === 'dark'
                              ? 'bg-blue-900/30 text-blue-300 border-blue-700/50'
                              : 'bg-blue-100 text-blue-700 border-blue-200'
                          }`}>
                            Imported
                          </span>
                        ) : (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                            theme === 'dark'
                              ? 'bg-green-900/30 text-green-300 border-green-700/50'
                              : 'bg-green-100 text-green-700 border-green-200'
                          }`}>
                            Manual
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="col-span-2">
                    <div className="space-y-1">
                      {guest.guest?.phone && (
                        <div className={`text-xs truncate ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`} title={guest.guest.phone}>
                          {guest.guest.phone}
                        </div>
                      )}
                      {guest.guest?.email && (
                        <div className={`text-xs truncate ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`} title={guest.guest.email}>
                          {guest.guest.email}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Companions Column */}
                  <div className="col-span-1 flex items-center">
                    <div className="flex items-center space-x-1">
                      {guest.companion_count > 0 ? (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                          theme === 'dark'
                            ? 'bg-purple-900/30 text-purple-300 border-purple-700/50'
                            : 'bg-purple-100 text-purple-700 border-purple-200'
                        }`} title={`${guest.companion_count} companion${guest.companion_count > 1 ? 's' : ''}`}>
                          {guest.companion_count}
                        </span>
                      ) : (
                        <span className={`text-xs ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          0
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Enhanced Status */}
                  <div className="col-span-1">
                    <span className={`inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-semibold shadow-sm border ${
                      guest.status === 'accepted' 
                        ? (theme === 'dark' 
                            ? 'bg-green-900/30 text-green-300 border-green-700/50' 
                            : 'bg-green-100 text-green-800 border-green-200')
                        : guest.status === 'rejected' 
                          ? (theme === 'dark' 
                              ? 'bg-red-900/30 text-red-300 border-red-700/50' 
                              : 'bg-red-100 text-red-800 border-red-200')
                          : guest.status === 'pending' 
                            ? (theme === 'dark' 
                                ? 'bg-yellow-900/30 text-yellow-300 border-yellow-700/50' 
                                : 'bg-yellow-100 text-yellow-800 border-yellow-200')
                            : (theme === 'dark' 
                                ? 'bg-gray-700 text-gray-300 border-gray-600' 
                                : 'bg-gray-100 text-gray-800 border-gray-200')
                    }`}>
                      {guest.status === 'accepted' ? 'Confirmed' :
                       guest.status === 'rejected' ? 'Declined' :
                       guest.status === 'pending' ? 'Pending' : 'No Response'}
                    </span>
                  </div>

                  {/* Actions Column */}
                  <div className="col-span-1 flex justify-center">
                    <button
                      onClick={() => onDeleteGuest(guest.id, guestName)}
                      className={`group relative px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 transform hover:scale-105 shadow-sm hover:shadow-md ${
                        theme === 'dark'
                          ? 'text-red-400 hover:text-white bg-red-900/20 hover:bg-red-500 border border-red-700/50 hover:border-red-500'
                          : 'text-red-600 hover:text-white bg-red-50 hover:bg-red-500 border border-red-200 hover:border-red-500'
                      }`}
                      title="Remove Guest"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <div className="space-y-6">
        {viewMode === 'grid' && renderGridView()}
        {viewMode === 'list' && renderListView()}
        {viewMode === 'table' && renderTableView()}
        
        {pagination && <Pagination {...pagination} />}
      </div>
      
      {commentModalOpen && editingGuest && (
        <GuestCommentModal
          isOpen={commentModalOpen}
          onClose={() => {
            setCommentModalOpen(false);
            setEditingGuest(null);
          }}
          guest={editingGuest}
          eventId={eventId}
          onUpdate={handleCommentUpdate}
          theme={theme}
          userRole={user?.role as 'admin' | 'host' | undefined}
        />
      )}
    </>
  );
};
